/*
 * The ingest card.
 *
 * It is a REPORT, not a gate. The commit has already happened by the time this
 * renders, so there is nothing here to approve — the card's job is to make the
 * change reviewable, and its primary action is revert. That inversion is what makes
 * running without approval gates safe in practice: the recovery path is a revert of
 * something you can read, rather than a prospective yes to something you cannot.
 *
 * Everything shown comes from `GET /history` and `GET /history/{op}/diff`. When
 * either is unavailable the card says which part is missing instead of rendering a
 * confident-looking summary of nothing.
 */
import { useState, type CSSProperties } from 'react'
import type { DiffFile, HistoryOperation, OperationDiff } from '../../../api/knowledgeBases.ts'
import { byteDelta, duration, num, shortSha } from '../lib/format.ts'
import { commitSummary, opLook } from '../lib/operations.ts'
import { UNDO_WINDOW_MS } from '../lib/useOperationRun.ts'
import { DiffView } from './DiffView.tsx'
import { FrontmatterTable } from './FrontmatterTable.tsx'

interface IngestCardProps {
  op: HistoryOperation | null
  diff: OperationDiff | null
  problem: string | null
  /** Milliseconds left on the undo window; null hides the undo affordance. */
  undoLeftMs: number | null
  reverting: boolean
  onRevert: () => void
  onDismissUndo: () => void
  onOpenInHistory: () => void
}

/** Files expanded by default — enough to see the shape without a wall of diff. */
const INITIALLY_OPEN = 1
/** Files listed before the card folds the rest behind a button. */
const INITIALLY_LISTED = 5

export function IngestCard({
  op,
  diff,
  problem,
  undoLeftMs,
  reverting,
  onRevert,
  onDismissUndo,
  onOpenInHistory,
}: IngestCardProps) {
  const [open, setOpen] = useState<Set<string>>(
    () => new Set((diff?.files ?? []).slice(0, INITIALLY_OPEN).map((file) => file.path)),
  )
  const [showAll, setShowAll] = useState(false)

  const files = diff?.files ?? []
  const listed = showAll ? files : files.slice(0, INITIALLY_LISTED)
  const look = op ? opLook(op.op) : opLook('ingest')

  function toggle(path: string) {
    setOpen((current) => {
      const next = new Set(current)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  return (
    <div className="ingest">
      <div className="ingest__head">
        <div className="ingest__title">
          <span className="ingest__ic" aria-hidden="true">
            ◈
          </span>
          {op ? `${look.label} завершён` : 'Операция завершена'}
          {op?.title ? <span className="ingest__subject">· {op.title}</span> : null}
        </div>
        <div className="ingest__stats">
          {op ? (
            <>
              {op.pages_created > 0 ? (
                <span className="pill pill--ok">+{num(op.pages_created)} новых</span>
              ) : null}
              {op.pages_updated > 0 ? (
                <span className="pill pill--info">~{num(op.pages_updated)} обновлено</span>
              ) : null}
              {op.pages_deleted > 0 ? (
                <span className="pill pill--danger">−{num(op.pages_deleted)} удалено</span>
              ) : null}
              {op.pages_created === 0 && op.pages_updated === 0 && op.pages_deleted === 0 ? (
                <span className="pill pill--neutral">wiki не изменилась</span>
              ) : null}
            </>
          ) : (
            <span className="pill pill--warn">сводка недоступна</span>
          )}
        </div>
      </div>

      {problem ? (
        <div style={{ padding: '11px 15px 0' }}>
          <div className="callout callout--warn">
            <b>Изменения применены, но отчёт собран не полностью.</b> {problem}
          </div>
        </div>
      ) : null}

      {files.length > 0 ? (
        <div className="ingest__files">
          {listed.map((file) => (
            <FileRow
              key={file.path}
              file={file}
              expanded={open.has(file.path)}
              onToggle={() => toggle(file.path)}
            />
          ))}
        </div>
      ) : diff ? (
        <div style={{ padding: '12px 15px', fontSize: 12, color: 'var(--mu2)' }}>
          Операция не изменила ни одного файла.
        </div>
      ) : null}

      <div className="ingest__foot">
        {undoLeftMs !== null ? (
          <span className="undo">
            <span
              className="undo__ring"
              aria-hidden="true"
              style={
                {
                  '--undo-progress': `${Math.max(0, Math.min(100, (undoLeftMs / UNDO_WINDOW_MS) * 100))}%`,
                } as CSSProperties
              }
            />
            <button
              type="button"
              className="btn btn--danger btn--sm"
              onClick={onRevert}
              disabled={reverting}
            >
              {reverting ? 'Откатываем…' : `Отменить · ${Math.ceil(undoLeftMs / 1000)} с`}
            </button>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onDismissUndo}>
              Оставить
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={onRevert}
            disabled={reverting || !op}
          >
            {reverting ? 'Откатываем…' : 'Откатить операцию'}
          </button>
        )}

        {files.length > INITIALLY_LISTED && !showAll ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowAll(true)}>
            Показать все {num(files.length)} файлов
          </button>
        ) : null}

        <button type="button" className="btn btn--ghost btn--sm" onClick={onOpenInHistory}>
          Открыть в истории
        </button>

        <span className="ingest__note">
          {op ? (
            <>
              {commitSummary(op)}
              {op.bytes_delta ? ` · ${byteDelta(op.bytes_delta)}` : ''}
              {op.finished_at ? ` · ${duration(op.started_at, op.finished_at)}` : ''}
            </>
          ) : (
            'диапазон коммитов неизвестен'
          )}
        </span>
      </div>
    </div>
  )
}

const CHANGE_SIGIL: Record<string, { sigil: string; className: string; label: string }> = {
  added: { sigil: '+', className: 'ifile__op--added', label: 'создан' },
  modified: { sigil: '~', className: 'ifile__op--modified', label: 'изменён' },
  deleted: { sigil: '−', className: 'ifile__op--deleted', label: 'удалён' },
  renamed: { sigil: '→', className: 'ifile__op--modified', label: 'переименован' },
}

function FileRow({
  file,
  expanded,
  onToggle,
}: {
  file: DiffFile
  expanded: boolean
  onToggle: () => void
}) {
  const change = CHANGE_SIGIL[file.change] ?? {
    sigil: '·',
    className: 'ifile__op--modified',
    label: file.change,
  }
  const counts = countLines(file)

  return (
    <div className="ifile">
      <button
        type="button"
        className="ifile__head"
        aria-expanded={expanded}
        onClick={onToggle}
        title={change.label}
      >
        <span className="ifile__caret" aria-hidden="true">
          ▶
        </span>
        <span className={`ifile__op ${change.className}`} aria-hidden="true">
          {change.sigil}
        </span>
        <span className="ifile__path">{file.path}</span>
        <span className="ifile__delta">
          {counts.added > 0 ? `+${counts.added}` : ''}
          {counts.added > 0 && counts.removed > 0 ? ' ' : ''}
          {counts.removed > 0 ? `−${counts.removed}` : ''}
          {counts.added === 0 && counts.removed === 0 ? '±0' : ''}
        </span>
      </button>
      {expanded ? (
        <div className="ifile__body">
          {(file.frontmatter_changes ?? []).length > 0 ? (
            <div style={{ marginBottom: 11 }}>
              <span className="kicker" style={{ display: 'block', marginBottom: 7 }}>
                Frontmatter
              </span>
              <FrontmatterTable changes={file.frontmatter_changes ?? []} />
            </div>
          ) : null}
          <span className="kicker" style={{ display: 'block', marginBottom: 7 }}>
            Тело
          </span>
          <DiffView hunks={file.hunks ?? []} mode="unified" />
        </div>
      ) : null}
    </div>
  )
}

export function countLines(file: DiffFile): { added: number; removed: number } {
  let added = 0
  let removed = 0
  for (const hunk of file.hunks ?? []) {
    for (const line of hunk.lines ?? []) {
      if (line.kind === 'add') added += 1
      else if (line.kind === 'del') removed += 1
    }
  }
  return { added, removed }
}

/** Short sha of whatever range the op produced, for a compact caption. */
export function opRangeLabel(op: HistoryOperation): string {
  if (!op.git_commit_first) return '—'
  const first = shortSha(op.git_commit_first)
  const last = shortSha(op.git_commit_last)
  return first === last ? first : `${first}‥${last}`
}
